import { useEffect, useRef } from 'react';

// Tell TypeScript about the custom web component element
declare global {
  namespace JSX {
    interface IntrinsicElements {
      'dotlottie-wc': React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement>,
        HTMLElement
      > & {
        src?: string;
        autoplay?: boolean | string;
        loop?: boolean | string;
        style?: React.CSSProperties;
      };
    }
  }
}

export default function HeroSection() {
  const titleRef = useRef<HTMLDivElement>(null);
  const lottieWrapRef = useRef<HTMLDivElement>(null); // kept for potential future use

  useEffect(() => {
    const el = titleRef.current;
    if (!el) return;
    el.style.opacity = '0';
    el.style.transform = 'translateY(30px)';
    setTimeout(() => {
      el.style.transition = 'opacity 0.8s ease, transform 0.8s ease';
      el.style.opacity = '1';
      el.style.transform = 'translateY(0)';
    }, 100);
  }, []);

  return (
    <section
      className="relative min-h-screen overflow-hidden flex flex-col"
      style={{ backgroundColor: '#FEF7EB' }}
    >
      {/* Decorative blobs */}
      <div
        className="absolute inset-0 pointer-events-none select-none overflow-hidden"
        style={{ zIndex: 0 }}
      >
        <div
          className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] rounded-full opacity-30"
          style={{ backgroundColor: '#E4D6EA', filter: 'blur(80px)' }}
        />
        <div
          className="absolute bottom-[10%] left-[-5%] w-[400px] h-[400px] rounded-full opacity-25"
          style={{ backgroundColor: '#0073E5', filter: 'blur(100px)' }}
        />
        <div
          className="absolute top-[40%] left-[30%] w-[300px] h-[300px] rounded-full opacity-20"
          style={{ backgroundColor: '#D4F419', filter: 'blur(80px)' }}
        />
      </div>

      {/* Floating accent pills */}
      <div
        className="absolute inset-0 pointer-events-none select-none overflow-hidden"
        style={{ zIndex: 10 }}
      >
        <div
          className="absolute top-[18%] left-[8%] px-4 py-2 rounded-full text-xs font-semibold text-black hidden md:flex items-center gap-2"
          style={{ backgroundColor: '#D4F419' }}
        >
          <i className="ri-global-line"></i> 10+ Markets
        </div>
        <div
          className="absolute top-[30%] right-[10%] px-4 py-2 rounded-full text-xs font-semibold text-white hidden md:flex items-center gap-2"
          style={{ backgroundColor: '#0073E5' }}
        >
          <i className="ri-code-s-slash-line"></i> 200+ Projects Done
        </div>
        <div
          className="absolute bottom-[28%] left-[12%] px-4 py-2 rounded-full text-xs font-semibold text-black hidden md:flex items-center gap-2"
          style={{ backgroundColor: '#F4971E' }}
        >
          <i className="ri-team-line"></i> 50+ Happy Clients
        </div>
        <div
          className="absolute bottom-[20%] right-[8%] px-4 py-2 rounded-full text-xs font-semibold text-black hidden md:flex items-center gap-2"
          style={{ backgroundColor: '#E4D6EA' }}
        >
          <i className="ri-rocket-line"></i> Full-cycle Development
        </div>
      </div>

      {/* Main content */}
      <div
        className="relative flex flex-col justify-center items-center flex-1 pt-32 pb-20 px-6 md:px-16 w-full"
        style={{ zIndex: 20 }}
      >
        <div ref={titleRef} className="text-center flex flex-col items-center">
          <div className="mt-24">
            <h1 className="text-6xl md:text-8xl lg:text-9xl font-bold tracking-tight text-black leading-none mb-2">
              Software that
            </h1>
            <h1
              className="text-6xl md:text-8xl lg:text-9xl font-bold tracking-tight leading-none mb-10"
              style={{ color: '#0073E5' }}
            >
              drives growth
            </h1>

            <a
              href="/contact-us"
              className="inline-flex items-center gap-2 text-white font-semibold px-8 py-4 rounded-full transition-colors duration-200 cursor-pointer whitespace-nowrap text-sm"
              style={{ backgroundColor: '#0073E5' }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = '#005bbf')
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = '#0073E5')
              }
            >
              Start a project
              <i className="ri-arrow-right-line text-base"></i>
            </a>
          </div>

          {/* Lottie web component */}
          <div ref={lottieWrapRef} className="pointer-events-none select-none">
            <dotlottie-wc
              src="https://lottie.host/b760429c-4772-46b6-a39b-c608fc6d3c08/zycpBFPPQT.lottie"
              autoplay
              loop
              style={{ width: '720px', height: '720px', display: 'block' }}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
